import React, { useEffect, useState } from "react";
import { Button, Box, useColorModeValue } from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import FadeSpinner from "../components/feedback/FadeSpinner";
import { ImLocation } from "react-icons/im";
import { FaMotorcycle } from "react-icons/fa";
import moment from "moment";
import { Helmet } from "react-helmet";

const SeeAllFeaturedBikes = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchBikes = async () => {
      try {
        setLoading(true);
        const carsRef = collection(db, "featured-bikes");
        const qSnap = await getDocs(carsRef);
        const bikesArray = [];
        qSnap.forEach((doc) => {
          bikesArray.push({ id: doc.id, ...doc.data() });
        });
        setBikes(bikesArray);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to load featured bikes.");
      }
    };

    fetchBikes();
  }, []);

  const checkDetails = (id) => {
    navigate(`/category/sale/${id}`, { state: { type: "featured-bike" } });
  };

  if (loading) return <FadeSpinner />;
  if (error) return <Box className="text-center text-red-500 p-8">{error}</Box>;
  if (bikes.length === 0) return (
    <Box className="text-center p-8">
      <h1 className="text-2xl text-gray-600">No featured bikes available</h1>
      <Link to="/" className="text-blue-500 hover:underline">Browse other bikes</Link>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Featured Motorcycles | NCP Wheels</title>
        <meta
          name="keywords"
          content="property for sale in pakistan, properties for sale in pakistan, houses for sale in pakistan, house for sale in pakistan, plots for sale in pakistan, residential plots for sale in pakistan, commercial plots for sale in pakistan, apartments for sale in pakistan, flats for sale in pakistan, villas for sale in pakistan, shops for sale in pakistan, commercial property for sale in pakistan, land for sale in pakistan, farm house for sale in pakistan, real estate pakistan, property dealers in pakistan, property investment in pakistan, cheap houses for sale in pakistan, luxury houses for sale in pakistan, property in pakistan for sale, property for rent in pakistan, houses for rent in pakistan, house for rent in pakistan, flats for rent in pakistan, apartments for rent in pakistan, shops for rent in pakistan, offices for rent in pakistan, commercial property for rent in pakistan, rooms for rent in pakistan, hostel for rent in pakistan, warehouse for rent in pakistan, portion for rent in pakistan, upper portion for rent in pakistan, lower portion for rent in pakistan, furnished apartment for rent in pakistan, rental property in pakistan, property on rent in pakistan, property for sale in karachi, houses for sale in karachi, flats for sale in karachi, plots for sale in karachi, apartments for sale in karachi, property for rent in karachi, houses for rent in karachi, flats for rent in karachi, property for sale in lahore, houses for sale in lahore, plots for sale in lahore, apartments for sale in lahore, DHA lahore houses for sale, bahria town lahore houses for sale, property for rent in lahore, houses for rent in lahore, flats for rent in lahore, property for sale in islamabad, houses for sale in islamabad, plots for sale in islamabad, apartments for sale in islamabad, DHA islamabad property for sale, bahria town islamabad property for sale, property for rent in islamabad, houses for rent in islamabad, flats for rent in islamabad, property for sale in rawalpindi, houses for sale in rawalpindi, plots for sale in rawalpindi, property for rent in rawalpindi, property for sale in peshawar, houses for sale in peshawar, plots for sale in peshawar, property for rent in peshawar, property for sale in swat, houses for sale in swat, plots for sale in swat, land for sale in swat, commercial property for sale in swat, property for rent in swat, houses for rent in swat, shops for rent in swat, property for sale in multan, houses for sale in multan, plots for sale in multan, property for rent in multan, property for sale in faisalabad, houses for sale in faisalabad, plots for sale in faisalabad, property for rent in faisalabad, property for sale in quetta, houses for sale in quetta, plots for sale in quetta, property for rent in quetta, property for sale in hyderabad pakistan, houses for sale in hyderabad pakistan, plots for sale in hyderabad pakistan, property for rent in hyderabad pakistan, property for sale in gujranwala, houses for sale in gujranwala, plots for sale in gujranwala, property for rent in gujranwala, property for sale in sialkot, houses for sale in sialkot, plots for sale in sialkot, property for rent in sialkot, property for sale in abbottabad, houses for sale in abbottabad, plots for sale in abbottabad, property for rent in abbottabad, property for sale in murree, houses for sale in murree, cottages for sale in murree, apartments for rent in murree, guest house for rent in murree, farmhouse for rent in pakistan, wedding lawn for rent in pakistan, plaza for sale in pakistan, plaza for rent in pakistan, building for sale in pakistan, building for rent in pakistan, office space for rent in pakistan, co working space in pakistan, real estate agents in pakistan, property dealers near me, zameen property pakistan, olx property pakistan, urgent house for sale in pakistan, installment plots in pakistan, easy installment houses in pakistan, overseas pakistanis property investment, pakistan real estate market, buy property in pakistan, sell property in pakistan, rent property in pakistan"
        />
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Featured Motorcycles
            </h1>
            <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {bikes.map((bike) => (
              <div 
                key={bike.id}
                className={`bg-${cardBg} rounded-xl overflow-hidden border border-opacity-20 border-gray-300 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl`}
                style={{ boxShadow: shadowColor }}
              >
                {/* Image with hover effect */}
                <div className="relative group overflow-hidden h-48">
                  <img
                    src={bike.carImages?.[0] || "/placeholder-bike.jpg"}
                    alt={bike.modelName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Badges */}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    {bike.offer && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-md transform hover:scale-110 transition-transform">
                        Offer
                      </span>
                    )}
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                      Featured
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {bike.modelName}
                      </h2>
                      <p className="text-blue-600 text-sm">{bike.manufacturer}</p>
                    </div>
                    {bike?.timestamp && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {moment(bike?.timestamp?.toDate()).fromNow()}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center text-gray-600 mb-3">
                    <ImLocation className="inline mr-1 text-blue-500" size={14} />
                    <span className="text-sm">{bike.location || bike.city}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Mileage:</span> {bike.mileage || 'N/A'} Km
                      </p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Year:</span> {bike.modelYear || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <p className="text-lg font-bold text-blue-700">
                      PKR {bike.offer ? bike.discountedPrice : bike.price}
                    </p>
                    {bike.offer && (
                      <p className="text-sm line-through text-gray-500">
                        PKR {bike.price}
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={() => checkDetails(bike.id)}
                    width="full"
                    className="transition-all duration-300 transform hover:scale-[1.02] hover:shadow-md"
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    color="white"
                    leftIcon={<FaMotorcycle />}
                    _hover={{
                      bgGradient: "linear(to-r, blue.600, blue.700)",
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    _active={{
                      bgGradient: "linear(to-r, blue.700, blue.800)",
                    }}
                    size="md"
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SeeAllFeaturedBikes;